import { Navigate, Route, Routes } from 'react-router-dom';

import { IS_PROD } from '@muzly/shared';

import { BootDismissOutlet } from './BootDismissOutlet';
import { RequireProfile } from './RequireProfile';
import { DebugConsolePage } from '../features/debug';
import { HomePage } from '../features/home';
import { LINK_ROUTE, LinkPage } from '../features/link';
import { DocPage, MorePage } from '../features/more';
import {
  HistoryPage,
  OnboardingPage,
  ResultDetailPage,
  SetupPage,
} from '../features/onboarding';
import { ProfilePage } from '../features/profile';
import { TabLayout } from '../layouts';

interface AppRoutesProps {
  /** 테스트 주입점. 기본은 빌드의 `IS_PROD`. */
  isProd?: boolean;
}

export const AppRoutes = ({ isProd = IS_PROD }: AppRoutesProps = {}) => {
  return (
    <Routes>
      {/* 게이트 밖 화면은 곧바로 그려지므로 첫 렌더에 부팅 대기 화면을 걷는다. 게이트 안은
          RequireProfile 이 복원을 마치고 열 때 걷는다. */}
      <Route element={<BootDismissOutlet />}>
        {/* 초기 설정 — 프로필이 없는 동안 앱의 유일한 화면. 게이트 밖에 있어야 게이트가
            여기로 보낼 수 있다. 탭바 없는 몰입 화면. */}
        <Route path="setup" element={<SetupPage />} />

        {/* 공유 링크 게이트웨이 — 앱이 있으면 앱, 없으면 웹. 게이트 밖이다: 프로필이 없어도 받아서
            목적지로 넘기고, 목적지가 게이트 안이면 그쪽이 초기 설정으로 보낸다. */}
        <Route path={LINK_ROUTE.slice(1)} element={<LinkPage />} />

        {/* 엔진 관찰·튜닝·검증 디버그 콘솔 (web↔mobile↔native 풀체인). 게이트 밖 — 개발 도구.
            운영 빌드에는 등록하지 않는다 — 게이트 밖이라 딥링크·주소 입력 한 번이면 열린다. */}
        {!isProd && <Route path="debug" element={<DebugConsolePage />} />}
      </Route>

      {/* 프로필이 있어야 열리는 안쪽. */}
      <Route element={<RequireProfile />}>
        {/* 탭 셸 — 홈이 앱의 첫 화면이고, 진단은 홈 카드에서 들어간다. 더보기 아래에
            프로필 수정이 산다(시안 탭 `홈`/`더보기`). */}
        <Route element={<TabLayout />}>
          <Route index element={<HomePage />} />
          <Route path="more" element={<MorePage />} />
          <Route path="more/profile" element={<ProfilePage />} />
          {/* 마이페이지의 문서(약관·방침·공지·FAQ·문의·라이선스). 드릴다운이라 탭바를 감춘다. */}
          <Route path="more/docs/:id" element={<DocPage />} />

          {/* 아래 셋은 탭바를 감추는 몰입 화면이지만 라우트는 셸 **안**이다.
              밖에 두면 진단으로 들어갈 때 탭 서브트리가 통째로 unmount되고, 그
              상태로 View Transitions 스냅샷이 잡혀 WKWebView에서 깜빡인다
              (`epyt-page-transition-reconciler-fix` F2). 감추는 쪽은 TabLayout이
              `fullscreenPaths`로 판정한다. */}

          {/* 진단 회차(회차 질문 + 녹음 + 결과). */}
          <Route path="diagnosis" element={<OnboardingPage />} />

          {/* 진단 회차 이력. */}
          <Route path="history" element={<HistoryPage />} />

          {/* 회차 하나의 지표 상세(시안 §8.2). 탭 이름이 주소에 있어야 공유한 링크가
              어느 지표를 말하는지까지 담는다. 탭 없이 열면 첫 탭으로 선다. */}
          <Route path="result/:id" element={<ResultDetailPage />} />
          <Route path="result/:id/:tab" element={<ResultDetailPage />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Route>
    </Routes>
  );
};
